import { useQuery } from '@tanstack/react-query'
import { Skeleton } from 'antd'
import type { JSX, ReactNode } from 'react'

import { SYSTEMIC_ONBOARDING_STATE_LABELS } from '../General/constants'
import { isBlockingTexasAreaCodes } from '../General/utils'
import { useIsFrostOn } from '../hooks/useIsFrostOn'
import {
  StyledCard,
  StyledCardBody,
  StyledCardHeader,
  StyledLinkButton,
  StyledRow,
  StyledRowDescription,
  StyledRowLabel,
  StyledRowText,
  StyledRowValue,
  StyledValueDivider,
} from '../Settings/styled'

import { Client, getContentReview } from 'api/clients'
import { AGE } from 'constants/age'
import { QUERIES } from 'constants/queries'
import { ROUTES } from 'constants/routes'
import { useSystemicOnboardingQuery } from 'hooks/useSystemicOnboardingQuery'
import { route } from 'utils/router'

const onOff = (value: boolean) => (value ? 'On' : 'Off')

const VALUE_SKELETON = <Skeleton.Input active size="small" style={{ width: 60, minWidth: 60 }} />

type SettingRowProps = {
  label: string
  description: string
  value: ReactNode
  to: string
  actionLabel?: string
}

function SettingRow({ label, description, value, to, actionLabel = 'Edit' }: SettingRowProps): JSX.Element {
  return (
    <StyledRow>
      <StyledRowText>
        <StyledRowLabel>{label}</StyledRowLabel>
        <StyledRowDescription>{description}</StyledRowDescription>
      </StyledRowText>
      <StyledRowValue>
        {value}
        <StyledValueDivider aria-hidden="true" />
        <StyledLinkButton aria-label={`${actionLabel} ${label}`} to={to}>
          {actionLabel}
        </StyledLinkButton>
      </StyledRowValue>
    </StyledRow>
  )
}

export function KeySettings({ client }: { client: Client }): JSX.Element {
  const generalPath = route(ROUTES.CLIENT.GENERAL, { id: client.id })
  const membersCompliancePath = route(ROUTES.CLIENT.MEMBERS_COMPLIANCE, { id: client.id })
  const { isFrostOn, isLoading: isFrostLoading } = useIsFrostOn(client.id)
  const contentReview = useQuery([QUERIES.CONTENT_REVIEW, { id: client.id }], () => getContentReview(client.id))
  const systemicOnboarding = useSystemicOnboardingQuery({ clientId: client.id })

  function renderQueryValue(query: { isLoading: boolean; isError: boolean }, value: () => ReactNode): ReactNode {
    if (query.isLoading) return VALUE_SKELETON
    if (query.isError) return 'Could not load'
    return value()
  }

  return (
    <StyledCard aria-labelledby="client-key-settings">
      <StyledCardHeader>
        <h3 id="client-key-settings">Key Settings</h3>
      </StyledCardHeader>
      <StyledCardBody>
        <SettingRow
          actionLabel="Configure"
          description="Web form (Frost) or Reply Y"
          label="Sign Up Experience"
          to={route(ROUTES.CLIENT.SIGN_UP, { id: client.id })}
          value={isFrostLoading ? VALUE_SKELETON : isFrostOn ? 'Web form' : 'Reply Y'}
        />
        <SettingRow
          description="Staff review campaigns before they send"
          label="Campaigns Require Review"
          to={membersCompliancePath}
          value={renderQueryValue(contentReview, () => onOff(Boolean(contentReview.data)))}
        />
        <SettingRow
          description="Members with Texas area codes cannot opt in"
          label="Block Texas Area Codes"
          to={membersCompliancePath}
          value={onOff(isBlockingTexasAreaCodes(client.unsupportedStateCodes))}
        />
        <SettingRow
          description="Remove Community branding from member screens"
          label="White-labeling"
          to={generalPath}
          value={onOff(client.whiteLabelEnabled ?? false)}
        />
        <SettingRow
          description="Raising the age can remove members"
          label="Minimum Fan Age"
          to={membersCompliancePath}
          value={client.minimumFanAge || AGE.MIN_AGE}
        />
        <SettingRow
          description="Subscriptions created through the API"
          label="Systemic Onboarding"
          to={membersCompliancePath}
          value={renderQueryValue(
            systemicOnboarding,
            () => SYSTEMIC_ONBOARDING_STATE_LABELS[systemicOnboarding.data?.data?.state] || 'Not set',
          )}
        />
      </StyledCardBody>
    </StyledCard>
  )
}
